import type { SanityImageObject } from "@sanity/image-url/lib/types/types";

import * as React from "react";

import Input from "@common/designSystem/Input";
import LabeledControl from "@common/designSystem/LabeledControl";
import { ErrorMessage as GlobalErrorMessage } from "@components/account/common";
import GoogleAuth from "@components/account/GoogleAuth";
import { FullScreenLoader } from "@components/common/FullScreenLoader";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { sanityClient } from "@editor/sanity/client";
import { useAllowScroll } from "@editor/utils/hooks/useAllowScroll";
import { routes } from "@editor/utils/router";
import { ReploLogoBlack } from "@svg/replo-logo-black";

import {
  Indicator,
  IndicatorButton,
  Indicators,
  Root,
  Slide,
  Slides,
  SlideTrack,
} from "@replo/carousel";
import { Accordion } from "@replo/design-system/components/accordion";
import Button from "@replo/design-system/components/button/Button";
import Separator from "@replo/design-system/components/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import imageUrlBuilder from "@sanity/image-url";
import { useQuery } from "@tanstack/react-query";
import groq from "groq";
import { Navigate } from "react-router-dom";

import { AlreadyHaveAccountLink } from "./AlreadyHaveAccountLink";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import { useSignupForm } from "./useSignupForm";

const builder = imageUrlBuilder(sanityClient);

function urlFor(source: SanityImageObject) {
  return builder.image(source);
}

type FeatureItem = {
  _key: string;
  title?: string;
  description?: string;
  image?: SanityImageData;
  videoUrl?: string;
};

type FaqItem = {
  _key: string;
  question?: string;
  answer?: string;
};

type SanityImageData = SanityImageObject & { alt?: string };

type SignupLandingPageData = {
  _id: string;
  title?: string;
  introText?: string;
  learnMoreButtonText?: string;
  heroImages?: SanityImageData[];
  meetReploSection?: {
    title?: string;
    description?: string;
    features?: FeatureItem[];
  };
  faqSection?: {
    title?: string;
    faqItems?: FaqItem[];
  };
};

const signupLandingPageQuery = groq`*[_type == "signupLandingPage" && slug.current == "default"][0]{
  _id,
  title,
  introText,
  learnMoreButtonText,
  heroImages[]{asset, alt, hotspot, crop},
  meetReploSection{
    title,
    description,
    features[]{_key, title, description, image{asset, alt, hotspot, crop}, videoUrl}
  },
  faqSection{
    title,
    faqItems[]{_key, question, answer}
  }
}`;

const LOGOS = [
  {
    src: "/images/customers/caraway.png",
    alt: "Caraway",
    width: 105,
    height: 48,
  },
  {
    src: "/images/customers/vayner.png",
    alt: "Vayner Media",
    width: 120,
    height: 48,
  },
  {
    src: "/images/customers/studs.png",
    alt: "Studs",
    width: 105,
    height: 48,
  },
  {
    src: "/images/customers/supply.png",
    alt: "Supply",
    width: 105,
    height: 48,
  },
  {
    src: "/images/customers/jones-road.png",
    alt: "Jones Road",
    width: 105,
    height: 48,
  },
  {
    src: "/images/customers/hexclad.png",
    alt: "HexClad",
    width: 105,
    height: 48,
  },
];

function Logos() {
  return (
    <div className="mx-auto grid max-w-lg grid-cols-3 items-center justify-items-center gap-6 sm:max-w-xl sm:grid-cols-6 lg:max-w-none">
      {LOGOS.map((logo, index) => (
        <div key={index} className="max-h-12 w-full object-contain">
          <img
            src={logo.src}
            alt={logo.alt}
            width={logo.width}
            height={logo.height}
          />
        </div>
      ))}
    </div>
  );
}

const SignUpLandingPage = () => {
  const { isAuthenticated, isLoading: isLoadingUser } = useCurrentUser();
  const heroCarouselId = React.useId();
  useAllowScroll();

  const {
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
  } = useSignupForm();

  const meetReploSectionRef = React.useRef<HTMLDivElement>(null);

  const {
    data: sanityData,
    isLoading: isLoadingSanity,
    error: sanityFetchError,
  } = useQuery<SignupLandingPageData | null>({
    queryKey: ["signupLandingPage"],
    queryFn: async () =>
      sanityClient.fetch<SignupLandingPageData>(signupLandingPageQuery),
  });

  if (isLoadingUser || isLoadingSanity) {
    return <FullScreenLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={routes.home.root} replace />;
  }

  if (sanityFetchError || !sanityData) {
    console.error("Failed to fetch Sanity data:", sanityFetchError);
    return <Navigate to={routes.signup} replace />;
  }

  return (
    <div className="flex min-h-screen w-full justify-center bg-white p-4">
      <div className="w-full max-w-7xl">
        <div className="flex w-full flex-col items-center bg-white">
          <div className="flex w-full items-start justify-start">
            {/* Left Side: Signup Form Area */}
            <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-10 lg:max-w-xl lg:py-20">
              <div className="flex w-full max-w-md flex-col items-center justify-start gap-10">
                {/* Logo Section */}
                <div className="flex w-full flex-col items-start gap-4">
                  <ReploLogoBlack />
                </div>

                {/* Intro Text */}
                {sanityData.introText && (
                  <p className="text-content-primary w-full text-left text-3xl font-bold">
                    {sanityData.introText}
                  </p>
                )}

                {/* Form */}
                <form
                  className="no-scrollbar flex w-full flex-col gap-8"
                  onSubmit={(data) => void handleSubmit(onSubmit)(data)}
                >
                  <GlobalErrorMessage errorMessage={displayErrorMessage} />
                  <div className="flex flex-col gap-4">
                    <LabeledControl
                      label="Email"
                      className="text-content-primary text-sm font-medium"
                      size="base"
                      id="email"
                      error={emailError}
                    >
                      <Input
                        id="email"
                        validityState={emailError ? "invalid" : undefined}
                        autoComplete="email"
                        placeholder="sample@email.com"
                        {...emailRegisterProps}
                        type="email"
                        size="base"
                      />
                    </LabeledControl>
                    <LabeledControl
                      label="Password"
                      className="text-content-primary text-sm font-medium"
                      size="base"
                      id="password"
                      error={passwordError}
                    >
                      <Input
                        id="password"
                        validityState={passwordError ? "invalid" : undefined}
                        autoComplete="new-password"
                        placeholder="••••••••••••"
                        {...passwordRegisterProps}
                        type="password"
                        size="base"
                      />
                    </LabeledControl>
                  </div>
                  <TermsAndPrivacy />
                  <div className="flex flex-col items-stretch gap-4">
                    <Button
                      layoutClassName="w-full"
                      variant="primary"
                      size="base"
                      type="submit"
                      isLoading={isLoadingCreateAccount}
                      disabled={buttonShouldBeDisabled}
                    >
                      Create Account
                    </Button>
                    <Separator className="my-0" />
                    <GoogleAuth
                      width={500}
                      handleGoogleCredentials={onGoogleSubmit}
                    />
                  </div>
                  <AlreadyHaveAccountLink />
                </form>
              </div>

              {/* Learn More Button */}
              {sanityData.learnMoreButtonText && (
                <Button
                  variant="secondary"
                  size="base"
                  onClick={() => {
                    meetReploSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                  layoutClassName="mt-16"
                >
                  {sanityData.learnMoreButtonText}
                  <svg
                    className="relative top-0.5 ml-1.5 h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 14 14"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 7l-5 5m0 0l-5-5m5 5V1"
                    />
                  </svg>
                </Button>
              )}
            </div>

            {/* Right Side: Hero Image Carousel and Logos */}
            <div className="hidden flex-1 flex-col items-center lg:flex">
              {/* Carousel container */}
              <div className="relative h-full w-full items-center justify-center overflow-hidden pt-14">
                {sanityData.heroImages && sanityData.heroImages.length > 0 ? (
                  <HeroImageCarousel
                    images={sanityData.heroImages}
                    carouselId={heroCarouselId}
                  />
                ) : null}
              </div>

              {/* Logos Section - Below carousel */}
              <div className="mt-16 flex w-full flex-col items-center gap-6 p-6">
                <div className="bg-sheet border-secondary flex w-full max-w-md items-center justify-center gap-2 rounded-full border py-1">
                  <div className="text-muted px-2 text-center text-sm">
                    Over 5000+ companies use Replo to improve ecommerce
                    conversion rates
                  </div>
                </div>
                <Logos />
              </div>
            </div>
          </div>

          {/* Meet Replo Section */}
          {sanityData?.meetReploSection && (
            <div ref={meetReploSectionRef}>
              <MeetReploSection sectionData={sanityData.meetReploSection} />
            </div>
          )}

          {/* FAQ Section */}
          {sanityData?.faqSection &&
            sanityData.faqSection.faqItems &&
            sanityData.faqSection.faqItems.length > 0 && (
              <FAQSection sectionData={sanityData.faqSection} />
            )}
        </div>
      </div>
    </div>
  );
};

const HeroImageCarousel = ({
  images,
  carouselId,
}: {
  images: SanityImageData[];
  carouselId: string;
}) => {
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Root autoPlayInterval={5000} id={carouselId}>
      <Slides
        aria-label="Hero image carousel"
        className="relative h-full w-full overflow-hidden"
      >
        <SlideTrack className="flex h-full">
          {images.map((image, index) => (
            <Slide
              key={image.asset?._ref ?? index}
              className="h-full w-full shrink-0"
            >
              {image?.asset ? (
                <img
                  src={urlFor(image).auto("format").url()}
                  alt={image.alt ?? "Hero Image"}
                  className="h-full w-full object-contain"
                />
              ) : null}
            </Slide>
          ))}
        </SlideTrack>
      </Slides>

      {images.length > 1 && (
        <Indicators className="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-2">
          {({ pages }) =>
            pages.map((page) => (
              <Indicator key={page.key} pageIndex={Number(page.key)}>
                <IndicatorButton
                  className={twMerge(
                    "h-2 w-2 rounded-full transition-colors",
                    page.isActive
                      ? "bg-indicator-active"
                      : "bg-indicator-inactive hover:bg-indicator-hover",
                  )}
                />
              </Indicator>
            ))
          }
        </Indicators>
      )}
    </Root>
  );
};

const MeetReploSection = ({
  sectionData,
}: {
  sectionData: NonNullable<SignupLandingPageData["meetReploSection"]>;
}) => {
  const [selectedFeatureIndex, setSelectedFeatureIndex] =
    React.useState<number>(0);
  const {
    title = "Meet Replo",
    description = "Build beautiful, high-performing web pages effortlessly. Design, collaborate, and launch faster—no coding required.",
    features = [],
  } = sectionData;

  const activeFeature = features[selectedFeatureIndex] ?? features[0];

  const getVideoUrl = (url: string) => {
    const videoUrl = new URL(url);
    videoUrl.searchParams.append("autoplay", "1");
    videoUrl.searchParams.append("mute", "1");
    return videoUrl.toString();
  };

  return (
    <div className="w-full bg-white p-6 pt-16 md:p-10 md:pt-24 lg:p-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-col items-center gap-4 text-center md:mb-16">
          <h2 className="text-content-primary text-3xl font-bold">{title}</h2>
          <p className="text-content-secondary max-w-2xl text-base">
            {description}
          </p>
        </div>
        <div className="flex flex-col items-center gap-10 md:flex-row md:gap-20">
          <div className="bg-surface-secondary aspect-video w-full overflow-hidden rounded-xl md:w-1/2">
            {activeFeature?.videoUrl && (
              <iframe
                className="h-full w-full"
                src={getVideoUrl(activeFeature.videoUrl)}
                title={title ?? "Meet Replo Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            )}
            {!activeFeature?.videoUrl && activeFeature?.image?.asset && (
              <img
                src={urlFor(activeFeature.image)
                  .width(600)
                  .auto("format")
                  .url()}
                alt={
                  activeFeature.image.alt ?? "Meet Replo visual representation"
                }
                className="h-full w-full object-cover"
              />
            )}
            {!activeFeature?.videoUrl && !activeFeature?.image?.asset && (
              <div className="flex h-full w-full items-center justify-center">
                <p className="text-content-tertiary">
                  Select a feature to see media
                </p>
              </div>
            )}
          </div>
          <div className="w-full md:w-1/2">
            <div className="flex flex-col">
              {features.map((feature, index) => (
                <div
                  key={feature._key}
                  className={`border-divider cursor-pointer border-b py-4 transition-colors duration-150 last:border-b-0 ${selectedFeatureIndex === index ? "bg-surface-hover" : "bg-white"}`}
                  onClick={() => setSelectedFeatureIndex(index)}
                >
                  <h3
                    className={twMerge(
                      "mb-1 text-xl font-semibold transition-colors duration-150",
                      selectedFeatureIndex === index
                        ? "text-content-primary"
                        : "text-content-tertiary",
                    )}
                  >
                    {feature.title}
                  </h3>
                  {selectedFeatureIndex === index && (
                    <p className="text-content-secondary text-base">
                      {feature.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection = ({
  sectionData,
}: {
  sectionData: NonNullable<SignupLandingPageData["faqSection"]>;
}) => {
  const { title = "Frequently asked questions", faqItems = [] } = sectionData;

  if (faqItems.length === 0) {
    return null;
  }

  const accordionElements = faqItems.map((item) => ({
    id: item._key,
    label: item.question ?? "",
    description: item.answer ?? "",
  }));

  return (
    <div className="w-full bg-white p-6 md:p-10 lg:p-20">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-content-primary mb-6 text-center text-3xl font-bold">
          {title}
        </h2>
        <Accordion elements={accordionElements} primaryColor="#334155" />
      </div>
    </div>
  );
};

export default SignUpLandingPage;
